import React, { useState, useEffect } from "react";
import { Clock, Receipt, FileText, Calendar, User, CheckCircle, XCircle, WarningCircle, Trash } from "@phosphor-icons/react";
import { api, formatCurrency, formatDate } from "../../utils/api";
import { Loading, EmptyState } from "../shared/EmptyState";
import { Badge } from "../shared/Badge";

export const Historial = ({ adminToken }) => {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todos");

  useEffect(() => {
    cargarHistorial();
  }, []);

  const cargarHistorial = async () => {
    try {
      // Obtener todos los datos
      const [presupuestos, facturas, visitas, clientes] = await Promise.all([
        api.get("/presupuestos", adminToken),
        api.get("/facturas", adminToken),
        api.get("/visitas-tecnicas", adminToken),
        api.get("/clientes", adminToken)
      ]);

      // Construir timeline
      const eventos = [];

      // Presupuestos
      presupuestos.forEach(p => {
        eventos.push({
          id: p.id,
          tipo: "presupuesto",
          fecha: p.fecha || p.created_at,
          titulo: `Presupuesto ${p.numero}`,
          descripcion: `${p.cliente_nombre} - ${formatCurrency(p.total)}`,
          estado: p.estado,
          icono: <FileText size={20} />,
          color: p.estado === "aceptado" ? "success" : p.estado === "rechazado" ? "danger" : "warning"
        });
      });

      // Facturas
      facturas.forEach(f => {
        eventos.push({
          id: f.id,
          tipo: "factura",
          fecha: f.fecha || f.created_at,
          titulo: `Factura ${f.numero}`,
          descripcion: `${f.cliente} - ${formatCurrency(f.total)}`,
          estado: f.cobrada ? "cobrada" : "pendiente",
          icono: <Receipt size={20} />,
          color: f.cobrada ? "success" : "warning"
        });
      });

      // Visitas técnicas
      visitas.forEach(v => {
        eventos.push({
          id: v.id,
          tipo: "visita",
          fecha: v.fecha_visita || v.created_at,
          titulo: "Visita Técnica",
          descripcion: `${v.cliente_nombre} - ${v.tipo_reforma}`,
          estado: "realizada",
          icono: <Calendar size={20} />,
          color: "info"
        });
      });

      // Clientes nuevos
      clientes.forEach(c => {
        eventos.push({
          id: c.id,
          tipo: "cliente",
          fecha: c.created_at,
          titulo: "Cliente Nuevo",
          descripcion: c.nombre,
          estado: "activo",
          icono: <User size={20} />,
          color: "accent"
        });
      });

      // Ordenar por fecha descendente
      eventos.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

      setHistorial(eventos);
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const historialFiltrado = historial.filter(evento => {
    if (filtro === "todos") return true;
    return evento.tipo === filtro;
  });

  const eliminarPresupuesto = async (id, numero) => {
    if (!window.confirm(`¿Eliminar el presupuesto ${numero}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/presupuestos/${id}`, adminToken);
      setHistorial((prev) => prev.filter((e) => !(e.tipo === "presupuesto" && e.id === id)));
    } catch (e) {
      console.error("Error eliminando presupuesto:", e);
      alert("No se pudo eliminar el presupuesto");
    }
  };

  const getIconoEstado = (estado) => {
    switch(estado) {
      case "aceptado":
      case "cobrada":
      case "activo":
        return <CheckCircle size={16} weight="fill" style={{ color: "var(--success)" }} />;
      case "rechazado":
        return <XCircle size={16} weight="fill" style={{ color: "var(--danger)" }} />;
      case "pendiente":
        return <WarningCircle size={16} weight="fill" style={{ color: "var(--warning)" }} />;
      default:
        return <Clock size={16} style={{ color: "var(--text-secondary)" }} />;
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="pb-24">
      <div className="container pt-4">
        <div className="mb-4">
          <h1 className="text-h2 font-display mb-1">📜 Historial</h1>
          <p className="text-small text-secondary">{historial.length} eventos registrados</p>
        </div>

        {/* Filtros */}
        <div className="tabs mb-4">
          <button onClick={() => setFiltro("todos")} className={`tab ${filtro === "todos" ? "tab-active" : ""}`}>
            Todos
          </button>
          <button onClick={() => setFiltro("presupuesto")} className={`tab ${filtro === "presupuesto" ? "tab-active" : ""}`}>
            📄 Presupuestos
          </button>
          <button onClick={() => setFiltro("factura")} className={`tab ${filtro === "factura" ? "tab-active" : ""}`}>
            🧾 Facturas
          </button>
          <button onClick={() => setFiltro("visita")} className={`tab ${filtro === "visita" ? "tab-active" : ""}`}>
            📅 Visitas
          </button>
          <button onClick={() => setFiltro("cliente")} className={`tab ${filtro === "cliente" ? "tab-active" : ""}`}>
            👤 Clientes
          </button>
        </div>

        {/* Timeline */}
        {historialFiltrado.length === 0 ? (
          <EmptyState 
            icon="📜" 
            title="Sin eventos" 
            description="El historial aparecerá aquí"
          />
        ) : (
          <div className="space-y-3">
            {historialFiltrado.map((evento, index) => {
              const fechaActual = new Date(evento.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
              const fechaAnterior = index > 0 ? new Date(historialFiltrado[index - 1].fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
              const mostrarFecha = fechaActual !== fechaAnterior;

              return (
                <React.Fragment key={evento.id}>
                  {mostrarFecha && (
                    <div className="text-label mt-4" style={{ color: "var(--brand-primary)" }}>
                      {fechaActual.toUpperCase()}
                    </div>
                  )}
                  
                  <div className="card p-4 relative">
                    {/* Línea de timeline */}
                    {index < historialFiltrado.length - 1 && !mostrarFecha && (
                      <div 
                        style={{
                          position: "absolute",
                          left: "28px",
                          top: "56px",
                          width: "2px",
                          height: "calc(100% + 12px)",
                          background: "var(--border-subtle)"
                        }}
                      />
                    )}

                    <div className="flex items-start gap-3">
                      {/* Icono */}
                      <div 
                        className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ 
                          background: `var(--${evento.color}-bg)`,
                          color: `var(--${evento.color})`
                        }}
                      >
                        {evento.icono}
                      </div>

                      {/* Contenido */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <div className="font-bold text-small">{evento.titulo}</div>
                          <div className="flex items-center gap-1 text-tiny text-secondary">
                            {getIconoEstado(evento.estado)}
                            <span>{new Date(evento.fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                        <div className="text-small text-secondary">{evento.descripcion}</div>
                        <div className="mt-2 flex items-center justify-between">
                          <Badge variant={evento.color} style={{ fontSize: "0.7rem" }}>
                            {evento.estado}
                          </Badge>
                          {evento.tipo === "presupuesto" && (
                            <button
                              className="icon-button"
                              title="Eliminar presupuesto"
                              onClick={() => eliminarPresupuesto(evento.id, evento.titulo.replace("Presupuesto ", ""))}
                              style={{ color: "var(--error)" }}
                            >
                              <Trash size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
